import type { DateTime } from 'luxon';
import { type ChangeEvent, createContext, type FC, type MouseEvent, type ReactElement, useState } from 'react';
import { usePagination } from '../../content/hooks';
import type { BrewerModel, Order, VisitAmsPartReportData, VisitsAmsPartsSortingFields } from '../../models';

interface IReportsContext {
  loading: boolean;
  setLoading: (val: boolean) => void;
  dataUpdate: boolean;
  toggleUpdate: () => void;
  data: VisitAmsPartReportData[];
  setData: (val: VisitAmsPartReportData[]) => void;
  page: number;
  setPage: (val: number) => void;
  limit: number;
  setLimit: (val: number) => void;
  order: Order;
  setOrder: (val: Order) => void;
  orderBy: VisitsAmsPartsSortingFields;
  setOrderBy: (val: VisitsAmsPartsSortingFields) => void;
  handlePageChange: (_event: any, newPage: number) => void;
  handleLimitChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (_event: MouseEvent, property: VisitsAmsPartsSortingFields) => void;
  count: number;
  setCount: (val: number) => void;
  fromDate: DateTime | null;
  setFromDate: (val: DateTime | null) => void;
  toDate: DateTime | null;
  setToDate: (val: DateTime | null) => void;
  brewer: BrewerModel | null;
  setBrewer: (val: BrewerModel | null) => void;
}

export const AmsPartsUsedContext = createContext<IReportsContext>({} as IReportsContext);

export const AmsPartsUsedProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [dataUpdate, setDataUpdate] = useState(false);
  const toggleUpdate = (): void => setDataUpdate(!dataUpdate);

  const {
    page,
    setPage,
    limit,
    setLimit,
    count,
    setCount,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    handlePageChange,
    handleLimitChange,
    handleRequestSort
  } = usePagination<VisitsAmsPartsSortingFields>(toggleUpdate, 'visitDate', 'desc');

  const [data, setData] = useState<VisitAmsPartReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState<DateTime | null>(null);
  const [toDate, setToDate] = useState<DateTime | null>(null);
  const [brewer, setBrewer] = useState<BrewerModel | null>(null);

  return (
    <AmsPartsUsedContext.Provider
      value={{
        data,
        setData,
        page,
        setPage,
        limit,
        setLimit,
        count,
        setCount,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        handlePageChange,
        handleLimitChange,
        handleRequestSort,
        loading,
        setLoading,
        dataUpdate,
        toggleUpdate,
        fromDate,
        setFromDate,
        toDate,
        setToDate,
        brewer,
        setBrewer
      }}
    >
      {children}
    </AmsPartsUsedContext.Provider>
  );
};
